import {
	Add,
	Cancel,
	Check,
	Delete,
	Download,
	DownloadForOffline,
	Downloading,
	Edit,
	Email,
	FactCheck,
	MoreVert,
	NotificationAdd,
	Payments,
	RemoveRedEye,
	Send,
} from "@mui/icons-material";
import {
	Badge,
	Box,
	Button,
	FormControl,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	ListSubheader,
	Divider,
	Select,
	SelectChangeEvent,
	Tooltip,
} from "@mui/material";
import {
	formatCurrencyNumber,
	formatDate,
	formatDateTime,
	getDocumentStatusesForFilter,
	canGenerateReminder,
} from "@repo/legacy/components/helper/Index.js";
import List from "@repo/legacy/components/shared/lists/List";
import ConfirmationDialog from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import ConfirmationWithMessageDialog from "@repo/legacy/dialogs/ConfirmationWithMessageForwardRefDialog";
import Dropdown from "@repo/legacy/components/shared/sections/Dropdown";
import {
	DocumentStatus,
	DocumentType,
	useDownloadDocumentXmlLazyQuery,
	useGetAllProjectsNameLazyQuery,
	useDownloadDocumentLazyQuery,
	type Project,
} from "@repo/server-api";
import { BoundAsyncMultiSelect, useAsyncSelectStaticListHook } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAllCommercialDocumentsDataSource } from "@repo/legacy/components/shared/datasources/AllCommercialDocumentsDataSource";
import { DownloadButton } from "@repo/shared/components";

import type {
	GridCellParams,
	GridColDef,
	GridRowId,
	GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import type { ConfirmationDialogHandle } from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import type { ConfirmationWithMessageDialogHandle } from "@repo/legacy/dialogs/ConfirmationWithMessageForwardRefDialog";
import type { CommercialDocumentSummary } from "@repo/shared-invoice/types";
import type { EmailDialogHandle } from "@repo/shared-invoice/dialogs/EmailForwardRefDialog";
import type { PreviewDocumentDialogHandle } from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import type { OptionType } from "@repo/server-api/types";
import type { InfoDialogHandle } from "@repo/legacy/dialogs/InfoForwardRefDialog";
import InfoDialog from "@repo/legacy/dialogs/InfoForwardRefDialog";
import { EmailDialog } from "../dialogs/EmailForwardRefDialog";
import { PreviewDocumentDialog } from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import { Invoice } from "../types/CommercialDocumentTypes";
import { DunningCasesHistoryDialog, DunningCasesHistoryDialogHandle } from "../dialogs/DunningCasesHistoryDialog";
import { PaymentsDialog, PaymentsDialogHandle } from "../dialogs/PaymentsForwardRefDialog";
import { XmlValidationDialog, XmlValidationDialogHandle } from "../dialogs/XmlValidationDialog";

type ProformaFilter = "both" | "hide" | "only";

export const InvoiceListPage = () => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const status = useParams<{ status: string }>().status || DocumentStatus.Draft;
	const apiRef = useGridApiRef();

	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	// Filter states
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProject, setSelectedProject] = useState<OptionType | null>(null);
	const [proformaFilter, setProformaFilter] = useState<ProformaFilter>("both");

	// Document type filter - default to Invoice only for overdue status
	const [documentTypeFields, setDocumentTypeFields] = useState<
		{ id: string; name: string; type: DocumentType }[]
	>(
		status.toUpperCase() === "OVERDUE"
			? [{ id: "INVOICE", name: t("invoice"), type: DocumentType.Invoice }]
			: [],
	);

	const allDocumentTypes = useMemo(
		() => [
			{
				id: "INVOICE",
				name: t("invoice"),
				type: DocumentType.Invoice,
			},
			{
				id: "CREDIT_NOTE",
				name: t("credit-note"),
				type: DocumentType.CreditNote,
			},
			{
				id: "CORRECTED_INVOICE",
				name: t("corrected-invoice"),
				type: DocumentType.CorrectedInvoice,
			},
			{
				id: "CORRECTED_CREDIT_NOTE",
				name: t("corrected-credit-note"),
				type: DocumentType.CorrectedCreditNote,
			},
		],
		[t],
	);

	const searchDocumentTypeHook = useAsyncSelectStaticListHook<{
		id: string;
		name: string;
		type: DocumentType;
	}>(allDocumentTypes);

	const [downloadDocument] = useDownloadDocumentLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [downloadDocumentXml] = useDownloadDocumentXmlLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [getAllProjects] = useGetAllProjectsNameLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			first: 100,
		},
		onCompleted(data) {
			if (data.allProjects?.nodes) {
				setProjects(data.allProjects.nodes as Project[]);
			}
		},
	});

	const getProjects = (searchName: string) => {
		getAllProjects({
			variables: {
				first: 100,
				name: searchName,
			},
		});
	};

	useEffect(() => {
		getAllProjects();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const allInvoicesDataSource = useAllCommercialDocumentsDataSource() as ReturnType<
		typeof useAllCommercialDocumentsDataSource
	> & {
		rows: CommercialDocumentSummary[];
	};

	const confirmationRef = useRef<ConfirmationDialogHandle | null>(null);
	const confirmationWithMessageRef = useRef<ConfirmationWithMessageDialogHandle | null>(null);
	const emailRef = useRef<EmailDialogHandle | null>(null);
	const previewDocumentRef = useRef<PreviewDocumentDialogHandle | null>(null);
	const paymentsRef = useRef<PaymentsDialogHandle | null>(null);
	const xmlValidationRef = useRef<XmlValidationDialogHandle | null>(null);
	const infoRef = useRef<InfoDialogHandle | null>(null);
	const dunningHistoryRef = useRef<DunningCasesHistoryDialogHandle | null>(null);

	const generateReminder = useCallback(
		(invoice: CommercialDocumentSummary) => {
			confirmationRef.current?.show({
				text: t("do-you-want-to-generate-reminder-for-this-invoice"),
				onYes: () => {
					if (allInvoicesDataSource.generateReminder)
						allInvoicesDataSource.generateReminder(invoice.id).then((res: any) => {
							if (
								res.data.generateReminder?.errors &&
								res.data.generateReminder.errors.length > 0
							)
								return;
							dunningHistoryRef.current?.refresh();
						});
				},
			});
		},
		[allInvoicesDataSource, confirmationRef, dunningHistoryRef, t],
	);

	const handleSendEntry = useCallback(
		(invoice: CommercialDocumentSummary, entryId: string) => {
			emailRef.current?.show({
				invoice: invoice as Invoice,
				id: invoice.id,
				entityId: entryId,
				onSend: (data) => {
					if (allInvoicesDataSource.sendCommercialDocumentEmail)
						allInvoicesDataSource.sendCommercialDocumentEmail(
							invoice.type,
							invoice.id,
							invoice.client.id,
							{
								recipients: data.recipients,
								ccRecipients: data.ccRecipients,
								bccRecipients: data.bccRecipients,
								subject: data.subject,
								body: data.body,
								attachments: data.attachments,
								shouldMergeAttachments: data.shouldMergeAttachments,
							},
							entryId,
						).then(() => {
							dunningHistoryRef.current?.refresh();
						});
				},
			});
		},
		[allInvoicesDataSource, emailRef, dunningHistoryRef],
	);

	const columns: GridColDef<CommercialDocumentSummary>[] = useMemo(
		() => [
			{
				field: "client.name",
				headerName: t("commercial-document"),
				headerAlign: "center",
				flex: 1,
				sortable: true,
				valueGetter: (value, row) => row.client.name,
				renderCell(params: GridCellParams<CommercialDocumentSummary>) {
					const dueDays =
						params.row.status === DocumentStatus.Paid
							? 10
							: dayjs(params.row.dueDate).diff(dayjs(), "day");
					return (
						<Box className="w-100 padding11">
							<Box className="w-60 left">
								<strong
									style={
										!params.row.number
											? {
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-start",
													padding: "10px",
													marginTop: "5px",
													float: "left",
												}
											: {}
									}
									className="font21">
									{params.row.client.name}
								</strong>
								{params.row.isProforma && (
									<Badge
										style={{
											justifyContent: "center",
											alignItems: "center",
											display: "flex",
											backgroundColor: "#E7E6E6",
											color: "#414040",
											borderRadius: "10px",
											padding: "5px 10px",
											fontSize: "12px",
											fontWeight: "bold",
											width: "70px",
											height: "20px",
											float: "right",
											marginTop: "15px",
											marginRight: "10px",
										}}>
										<span>{t("proforma")}</span>
									</Badge>
								)}
								{params.row.number && (
									<>
										<br />
										<div className="font12" style={{ marginTop: 5 }}>
											{params.row.type === DocumentType.Invoice
												? t("invoice-number")
												: t("credit-note-number")}{" "}
											{params.row.number}
											<span>
												{` ${t("from-date")}`} {formatDate(params.row.issueDate, i18n.language)}
											</span>
											<span
												className={
													dueDays < 0 ? "redText" : dueDays < 7 ? "orangeText" : "greyText"
												}>
												{" "}
												-{" "}
												{dueDays < 7 && dueDays >= 0
													? t("due-in-days", { count: dueDays })
													: t("due-on")}{" "}
												{formatDate(params.row.dueDate, i18n.language)}
											</span>
										</div>
									</>
								)}
							</Box>
							<Box className="w-40 right textRight">
								{params.row.status === DocumentStatus.Cancelled && (
									<IconButton
										onClick={() => {
											infoRef.current?.show({
												text: params.row.cancellationReason || t("no-cancellation-message"),
											});
										}}>
										<Badge
											style={{
												justifyContent: "center",
												alignItems: "center",
												display: "flex",
												backgroundColor: "#F8D7DA",
												color: "#842029",
												borderRadius: "10px",
												padding: "5px",
												fontSize: "10px",
												fontWeight: "bold",
												width: "70px",
												height: "15px",
												float: "right",
												marginTop: "-7px",
											}}>
											{t("document-status.cancelled")}
										</Badge>
									</IconButton>
								)}
								<strong
									style={
										!params.row.number
											? {
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													padding: "10px",
													marginTop: "5px",
												}
											: {}
									}
									className="font21">
									{formatCurrencyNumber(params.row.grossTotal)}
								</strong>
								{params.row.number &&
								params.row.leftTotal &&
								params.row.status !== DocumentStatus.Cancelled ? (
									<>
										<br />
										<div className="font12 greyText" style={{ marginTop: 5 }}>
											{t("for-payment")}: {formatCurrencyNumber(params.row.leftTotal)}
										</div>
									</>
								) : null}
							</Box>
							<div className="clear" />
						</Box>
					);
				},
			},
			{
				field: "project.name",
				headerName: t("project"),
				headerAlign: "center",
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.project?.name || "",
				renderCell: (params: GridCellParams) => {
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}>
							<span>{params.row.project?.name || "-"}</span>
						</div>
					);
				},
			},
			{
				field: "project.number",
				headerName: t("project-number"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.project?.number || "",
				renderCell: (params: GridCellParams) => {
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}>
							<span>{params.row.project?.number || "-"}</span>
						</div>
					);
				},
			},
			{
				field: "dueDate",
				headerName: t("due-date"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.dueDate,
				renderCell: (params: GridCellParams) => (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							height: "100%",
						}}>
						<span>{params.row.dueDate ? formatDate(params.row.dueDate, i18n.language) : "-"}</span>
					</div>
				),
			},
			{
				field: "createdDate",
				headerName: t("created-at"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.createdDate,
				renderCell: (params: GridCellParams) => (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							height: "100%",
						}}>
						<span>
							{params.row.createdDate ? formatDate(params.row.createdDate, i18n.language) : "-"}
						</span>
					</div>
				),
			},
			{
				field: "type",
				headerName: t("type"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.type,
				renderCell: (params: GridCellParams) => {
					const isInvoice =
						params.row.type === DocumentType.Invoice ||
						params.row.type === DocumentType.CorrectedInvoice;
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}>
							<Badge
								style={{
									justifyContent: "center",
									alignItems: "center",
									display: "flex",
									backgroundColor: isInvoice ? "#D1E7DD" : "#FFE5D0",
									color: isInvoice ? "#0F5132" : "#663C00",
									borderRadius: "10px",
									padding: "5px 10px",
									fontSize: "12px",
									fontWeight: "bold",
									width: "70%",
									height: "20px",
									margin: "7px auto",
									textAlign: "center",
								}}>
								<span>{t(params.row.type.toLocaleLowerCase())}</span>
							</Badge>
						</div>
					);
				},
			},
			{
				field: "sentDate",
				headerName: t("sent-date"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.sentDate,
				renderCell: (params: GridCellParams) => {
					const dateTime = params.row.sentDate
						? formatDateTime(params.row.sentDate, i18n.language)
						: "-";
					const isSent = !!params.row.sentDate;
					let date = "";
					let time = "";
					if (isSent) {
						const [datePart, timePart] = dateTime.split(" ");
						date = datePart;
						time = timePart;
					}

					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								alignItems: "center",
								textAlign: "center",
							}}>
							<span>
								{isSent ? (
									<>
										{date}
										<br />
										{time}
									</>
								) : (
									"-"
								)}
							</span>
						</div>
					);
				},
			},
			{
				field: "cancellationReversalDocumentSentDate",
				headerName: t("cancellation-sent-date"),
				headerAlign: "center",
				width: 120,
				renderHeader: () => (
					<span style={{ whiteSpace: "normal", lineHeight: 1.3, textAlign: "center" }}>
						{t("cancellation-sent-date")}
					</span>
				),
				sortable: true,
				valueGetter: (value, row) => row.cancellationReversalDocumentSentDate,
				renderCell: (params: GridCellParams) => {
					const dateTime = params.row.cancellationReversalDocumentSentDate
						? formatDateTime(params.row.cancellationReversalDocumentSentDate, i18n.language)
						: "-";
					const isSent = !!params.row.cancellationReversalDocumentSentDate;
					let date = "";
					let time = "";
					if (isSent) {
						const [datePart, timePart] = dateTime.split(" ");
						date = datePart;
						time = timePart;
					}

					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								alignItems: "center",
								textAlign: "center",
							}}>
							<span>
								{isSent ? (
									<>
										{date}
										<br />
										{time}
									</>
								) : (
									"-"
								)}
							</span>
						</div>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				headerAlign: "center",
				align: "right",
				sortable: false,
				width: 250,
				renderCell: (params: GridCellParams) => {
					const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
					const row = params.row;

					// Determine document state for action visibility
					const isDraft = row.status === DocumentStatus.Draft;
					const isCancelled = row.status === DocumentStatus.Cancelled;
					const isPaid = row.status === DocumentStatus.Paid;
					const isActive = !isDraft && !isCancelled && !isPaid;
					const isSent = isActive && !!row.sentDate;
					const isUnsent = isActive && !row.sentDate;
					const isOverdue =
						isSent && row.dueDate && dayjs(row.dueDate).isBefore(dayjs(), "day");
					const isRegularSent = isSent && !isOverdue;
					const isInvalid = row.isInvalid;
					const haveStornedDocument = isCancelled && row.stornId;

					const openDelete = (id: string) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-delete-this-document"),
							onYes: () => {
								if (allInvoicesDataSource.deleteData) allInvoicesDataSource.deleteData(id);
							},
						});
					};
					const openEdit = (p: CommercialDocumentSummary) => {
						if (p.type === DocumentType.Invoice)
							navigate(`/invoices/${status}/${encodeURIComponent(p.id)}`);
						else navigate(`/vouchers/${status}/${encodeURIComponent(p.id)}`);
					};
					const openFinalize = (p: CommercialDocumentSummary) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-finalize-this-document"),
							onYes: () => {
								const issueDate = dayjs().format("YYYY-MM-DD");
								if (allInvoicesDataSource.finalizeData)
									allInvoicesDataSource.finalizeData(p.id, issueDate);
							},
						});
					};
					const openEmail = (
						p: CommercialDocumentSummary,
						id: string,
						entityId?: string | null,
					) => {
						emailRef.current?.show({
							invoice: p as Invoice,
							id: id,
							entityId: entityId,
							onSend: (data) => {
								if (allInvoicesDataSource.sendCommercialDocumentEmail)
									allInvoicesDataSource.sendCommercialDocumentEmail(p.type, p.id, p.client.id, {
										recipients: data.recipients,
										ccRecipients: data.ccRecipients,
										bccRecipients: data.bccRecipients,
										subject: data.subject,
										body: data.body,
										attachments: data.attachments,
										shouldMergeAttachments: data.shouldMergeAttachments,
									});
							},
						});
					};

					const openPreview = async (id: string) => {
						let result = null;
						result = await downloadDocument({
							variables: {
								id: id,
								shouldDownload: false,
							},
						});

						if (!result || !result.data) {
							throw new Error("Download failed");
						}

						let url: string | undefined;
						url = result.data.downloadDocument;

						if (url) {
							previewDocumentRef.current?.show({
								url: url,
							});
						}
					};

					const startDownload = async (invoice: CommercialDocumentSummary) => {
						let result = null;
						result = await downloadDocument({
							variables: {
								id: invoice.id,
								shouldDownload: true,
							},
						});

						if (!result || !result.data) {
							throw new Error("Download failed");
						}

						let url: string | undefined;
						url = result.data.downloadDocument;

						if (url) {
							window.open(url, "_blank");
						}
					};

					const startDownloadXML = async (id: string) => {
						const result = await downloadDocumentXml({
							variables: {
								id: id,
							},
						});

						if (!result || !result.data) {
							throw new Error("Download failed");
						}

						let url: string | undefined;
						url = result.data.downloadDocumentXml;

						if (url) {
							window.open(url, "_blank");
						}
					};

					const openPayments = (p: CommercialDocumentSummary) => {
						paymentsRef.current?.show({
							invoiceId: p.id,
							type: p.type,
							grossTotal: p.grossTotal || 0,
							refresh: () => {
								allInvoicesDataSource.refresh?.();
							},
						});
					};

					const openValidateXml = (id: string) => {
						xmlValidationRef.current?.show(id);
					};

					const handleClick = (event: React.MouseEvent<HTMLElement>) => {
						setAnchorEl(event.currentTarget);
					};
					const handleClose = () => {
						setAnchorEl(null);
					};

					const cancelDocument = (invoice: CommercialDocumentSummary) => {
						confirmationWithMessageRef.current?.show({
							text: t("do-you-want-to-cancel-this-document"),
							onYes: (message: string) => {
								if (allInvoicesDataSource.cancelData)
									allInvoicesDataSource.cancelData(invoice.id, message);
							},
						});
					};

					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 0.5,
								height: "100%",
								pr: 1,
							}}>
							{/* === Primary Workflow Action === */}
							{isDraft && !isInvalid && (
								<Button
									size="small"
									variant="contained"
									color="success"
									startIcon={<Check />}
									onClick={() => openFinalize(row)}
									data-testid="finalize-invoice-button">
									{t("finalize")}
								</Button>
							)}
							{isUnsent && !isInvalid && (
								<Button
									size="small"
									variant="contained"
									color="primary"
									startIcon={<Send />}
									onClick={() => openEmail(row, row.id)}
									data-testid="send-invoice-button">
									{t("send")}
								</Button>
							)}
							{isRegularSent && !isInvalid && (
								<Button
									size="small"
									variant="contained"
									color="success"
									startIcon={<Payments />}
									onClick={() => openPayments(row)}
									data-testid="payments-button">
									{t("pay")}
								</Button>
							)}
							{isOverdue && !isInvalid && (
								<Button
									size="small"
									variant="outlined"
									color="warning"
									startIcon={<NotificationAdd />}
									onClick={() => dunningHistoryRef.current?.show(row)}
									data-testid="reminder-button">
									{t("generate-reminder")}
								</Button>
							)}

							{/* === Secondary Icon Action === */}
							{isDraft && (
								<Tooltip title={t("edit")}>
									<IconButton
										size="small"
										onClick={() => openEdit(row)}
										data-testid="edit-invoice-button">
										<Edit />
									</IconButton>
								</Tooltip>
							)}
							{(isUnsent || isRegularSent || isPaid) && !isInvalid && (
								<DownloadButton
									buttonProps={{ size: "small" }}
									onDownload={() => startDownload(row)}
									title={t("download-document")}
								/>
							)}
							{isOverdue && !isInvalid && (
								<Tooltip title={t("email")}>
									<IconButton
										size="small"
										onClick={() => openEmail(row, row.id)}>
										<Email />
									</IconButton>
								</Tooltip>
							)}

							{/* === More Actions Menu === */}
							<IconButton
								size="small"
								onClick={handleClick}
								data-testid="invoice-actions-menu-button">
								<MoreVert />
							</IconButton>
							<Menu
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "right",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "right",
								}}>
								{/* === Document actions === */}
								{/* Download PDF - when not already visible as icon */}
								{!isInvalid && !isUnsent && !isRegularSent && !isPaid && (
									<MenuItem
										onClick={() => {
											startDownload(row);
											handleClose();
										}}>
										<Download sx={{ mr: 2 }} /> {t("download-document")}
									</MenuItem>
								)}
								{/* Download XML */}
								{!isDraft && (
									<MenuItem
										onClick={() => {
											startDownloadXML(row.id);
											handleClose();
										}}>
										<DownloadForOffline sx={{ mr: 2 }} /> {t("download-invoice-xml")}
									</MenuItem>
								)}
								{/* Email - when not primary or icon */}
								{!isInvalid && !isUnsent && !isOverdue && (
									<MenuItem
										data-testid="send-email-menu-item"
										onClick={() => {
											openEmail(row, row.id);
											handleClose();
										}}>
										<Email sx={{ mr: 2 }} /> {t("email")}
									</MenuItem>
								)}
								{/* Preview */}
								{!isInvalid && (
									<MenuItem
										onClick={() => {
											openPreview(row.id);
											handleClose();
										}}>
										<RemoveRedEye sx={{ mr: 2 }} /> {t("preview")}
									</MenuItem>
								)}
								{/* Validate XML */}
								<MenuItem
									data-testid="validate-xml-menu-item"
									onClick={() => {
										openValidateXml(row.id);
										handleClose();
									}}>
									<FactCheck sx={{ mr: 2 }} /> {t("validate-xml")}
								</MenuItem>
								{/* Pay - when not primary (also for Paid, to allow corrections) */}
								{(isActive || isPaid) && !isRegularSent && !isInvalid && (
									<MenuItem
										data-testid="payments-menu-item"
										onClick={() => {
											openPayments(row);
											handleClose();
										}}>
										<Payments sx={{ mr: 2 }} /> {t("pay")}
									</MenuItem>
								)}
								{/* Generate Reminder - when not primary */}
								{canGenerateReminder(row) && !isOverdue && (
									<MenuItem
										data-testid="generate-reminder-menu-item"
										onClick={() => {
											dunningHistoryRef.current?.show(row);
											handleClose();
										}}>
										<NotificationAdd sx={{ mr: 2 }} /> {t("generate-reminder")}
									</MenuItem>
								)}
								{/* Cancel Document */}
								{!isCancelled &&
									!isDraft &&
									row.type !== DocumentType.CorrectedInvoice &&
									row.type !== DocumentType.CorrectedCreditNote && (
									<MenuItem
										data-testid="cancel-invoice-menu-item"
										onClick={() => {
											cancelDocument(row);
											handleClose();
										}}>
										<Cancel sx={{ mr: 2 }} /> {t("cancel-document")}
									</MenuItem>
								)}
								{/* Delete */}
								{isDraft && (
									<MenuItem
										data-testid="delete-invoice-menu-item"
										onClick={() => {
											openDelete(row.id);
											handleClose();
										}}>
										<Delete sx={{ mr: 2 }} /> {t("delete")}
									</MenuItem>
								)}

								{/* === Storno section - only when storno document exists === */}
								{haveStornedDocument && <Divider />}
								{haveStornedDocument && (
									<ListSubheader sx={{ lineHeight: "32px", fontSize: "0.75rem" }}>
										{t("cancellation")}
									</ListSubheader>
								)}
								{haveStornedDocument && !isInvalid && (
									<MenuItem
										onClick={() => {
											startDownload({ ...row, id: row.stornId });
											handleClose();
										}}>
										<Download sx={{ mr: 2 }} /> {t("download-document")}
									</MenuItem>
								)}
								{haveStornedDocument && (
									<MenuItem
										onClick={() => {
											startDownloadXML(row.stornId);
											handleClose();
										}}>
										<Downloading sx={{ mr: 2 }} /> {t("download-cancelled-invoice-xml")}
									</MenuItem>
								)}
								{haveStornedDocument && (
									<MenuItem
										onClick={() => {
											openEmail(row, row.stornId);
											handleClose();
										}}>
										<Email sx={{ mr: 2 }} /> {t("email-storned")}
									</MenuItem>
								)}
								{haveStornedDocument && (
									<MenuItem
										onClick={() => {
											openPreview(row.stornId);
											handleClose();
										}}>
										<RemoveRedEye sx={{ mr: 2 }} /> {t("preview-storned")}
									</MenuItem>
								)}
								{haveStornedDocument && (
									<MenuItem
										onClick={() => {
											openValidateXml(row.stornId);
											handleClose();
										}}>
										<FactCheck sx={{ mr: 2 }} /> {t("validate-xml-storned")}
									</MenuItem>
								)}
							</Menu>
						</Box>
					);
				},
			},
		],
		[t, i18n.language],
	);

	const allColumns = useMemo(() => {
		allInvoicesDataSource.setFilter?.({
			...allInvoicesDataSource.filter,
			status: getDocumentStatusesForFilter(status),
		});
		switch (status.toLocaleUpperCase()) {
			case DocumentStatus.Draft:
				return columns.filter(
					(col) =>
						col.field !== "sentDate" &&
						col.field !== "dueDate" &&
						col.field !== "createdDate" &&
						col.field !== "cancellationReversalDocumentSentDate",
				);
			case "UNSENT":
				return columns.filter(
					(col) =>
						col.field !== "sentDate" &&
						col.field !== "createdDate" &&
						col.field !== "cancellationReversalDocumentSentDate",
				);
			case DocumentStatus.Cancelled:
				return columns.filter((col) => col.field !== "dueDate");
			default:
				return columns.filter(
					(col) =>
						col.field !== "createdDate" && col.field !== "cancellationReversalDocumentSentDate",
				);
		}
	}, [status, columns, t, i18n.language]);

	// Memoize document types filter to prevent infinite re-renders
	const selectedDocumentTypes = useMemo(
		() =>
			documentTypeFields.length > 0
				? documentTypeFields.map((d) => d.type)
				: [
						DocumentType.Invoice,
						DocumentType.CreditNote,
						DocumentType.CorrectedInvoice,
						DocumentType.CorrectedCreditNote,
					],
		[documentTypeFields],
	);

	// Memoize proforma filter value
	const proformaFilterValue = useMemo(() => {
		if (proformaFilter === "only") return true;
		if (proformaFilter === "hide") return false;
		return null;
	}, [proformaFilter]);

	// Filter rows by selected project (frontend filtering until backend support is added)
	const filteredRows = useMemo(() => {
		if (!selectedProject?.item?.id) return allInvoicesDataSource.rows;
		return allInvoicesDataSource.rows.filter((row) => row.project?.id === selectedProject.item?.id);
	}, [allInvoicesDataSource.rows, selectedProject]);

	// Auto-size the actions column to fit content
	useEffect(() => {
		if (!apiRef.current || allInvoicesDataSource.isLoading || filteredRows.length === 0) return;
		const timer = setTimeout(() => {
			apiRef.current?.autosizeColumns({
				columns: ["actions"],
				includeHeaders: true,
				includeOutliers: true,
			});
		}, 100);
		return () => clearTimeout(timer);
	}, [apiRef, filteredRows, allInvoicesDataSource.isLoading]);

	// Update data source type filter when document types change
	useEffect(() => {
		if (allInvoicesDataSource.setFilter) {
			allInvoicesDataSource.setFilter({
				...allInvoicesDataSource.filter,
				isProforma: proformaFilterValue,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [proformaFilterValue]);

	return (
		<>
			<ConfirmationDialog ref={confirmationRef} />

			<ConfirmationWithMessageDialog ref={confirmationWithMessageRef} />

			<EmailDialog ref={emailRef} />

			<PreviewDocumentDialog ref={previewDocumentRef} />

			<PaymentsDialog ref={paymentsRef} />

			<XmlValidationDialog ref={xmlValidationRef} />

			<InfoDialog ref={infoRef} />

			<DunningCasesHistoryDialog
				ref={dunningHistoryRef}
				onGenerateReminder={generateReminder}
				onSendEntry={handleSendEntry}
			/>

			<h3 className="purpleHeader">{t(`invoices-${status}`)}</h3>
			<List
				gridApiRef={apiRef}
				columns={allColumns as GridCellParams<CommercialDocumentSummary>[]}
				redInvalidRows={true}
				dataSource={{ ...allInvoicesDataSource, rows: filteredRows }}
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={(newRowSelectionModel) => {
					setRowSelectionModel(newRowSelectionModel);
				}}
				type={selectedDocumentTypes}
				customRowHeight={{ getRowHeight: () => "auto" }}>
				<Box className="flex-between" sx={{ gap: 2, flexWrap: "wrap" }}>
					{/* Project Filter */}
					<Box sx={{ minWidth: 200 }}>
						<Dropdown
							dropdownType="project"
							doNotShowMenu
							elements={projects}
							element={{
								title: selectedProject?.title || "",
								inputValue: selectedProject?.inputValue || "",
								item: selectedProject?.item || null,
							}}
							setElement={(p) => {
								setSelectedProject(p);
							}}
							searchLabel={t("project")}
							searchPlaceholder={t("search-for-project")}
							getProjects={getProjects}
						/>
					</Box>

					{/* Document Type Multi-Select */}
					<Box sx={{ minWidth: 200 }}>
						<BoundAsyncMultiSelect
							label={t("document-type")}
							boundedValue={{
								value: documentTypeFields,
								onChangeValue: (v: { id: string; name: string; type: DocumentType }[]) =>
									setDocumentTypeFields(v),
								errorKey: "documentTypeFields",
							}}
							searchHook={searchDocumentTypeHook}
						/>
					</Box>

					{/* Proforma Filter */}
					<Box sx={{ minWidth: 180 }}>
						<FormControl fullWidth>
							<InputLabel id="proforma-filter-label">{t("proforma")}</InputLabel>
							<Select
								labelId="proforma-filter-label"
								value={proformaFilter}
								label={t("proforma")}
								onChange={(e: SelectChangeEvent<ProformaFilter>) => {
									setProformaFilter(e.target.value as ProformaFilter);
								}}>
								<MenuItem value="both">{t("both")}</MenuItem>
								<MenuItem value="hide">{t("hide-proforma")}</MenuItem>
								<MenuItem value="only">{t("only-proforma")}</MenuItem>
							</Select>
						</FormControl>
					</Box>

					{/* Create Buttons */}
					{status.toLocaleUpperCase() === DocumentStatus.Draft && (
						<Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
							<Button
								className="blueButton"
								data-testid="create-invoice-button"
								onClick={() => {
									navigate(`/invoices/${status}/new`);
								}}>
								{t("create-invoice")}
								<Add />
							</Button>
							<Button
								className="secondaryButton"
								variant="outlined"
								data-testid="create-credit-note-button"
								onClick={() => {
									navigate(`/vouchers/${status}/new`);
								}}>
								{t("create-credit-note")}
								<Add />
							</Button>
						</Box>
					)}
				</Box>
			</List>
		</>
	);
};
